<template>
  <v-container fluid class="app-container pa-0">
    <v-row no-gutters>
      <v-col cols="12" sm="12" md="6" class="scrollable-controls">
        <v-container>
          <v-card  v-if="$isSectionEnabled('design_maira','customize_widget')" class="elevation-0">
            <div class="d-flex flex-wrap align-center justify-center justify-md-start">
              <v-card-title class="font-weight-bold">
                {{ $vuetify.lang.t('$vuetify.add_maira.customize_widget') }}
              </v-card-title>

              <div class="d-flex align-center ml-md-auto">
                <v-btn
                  class="text-capitalize align-self-center mr-4"
                  color="primary"
                  @click="updateMairaOptions"
                  :disabled="!$hasProjectAccessFor([$pRole.ADMIN])"
                >
                  {{ $vuetify.lang.t('$vuetify.common.save') }}
                </v-btn>
                <v-dialog v-model="copyScriptTag" persistent max-width="700px">
                  <template v-slot:activator="{ attrs }">
                    <v-btn
                      class="text-capitalize align-self-center"
                      color="primary"
                      v-bind="attrs"
                      :disabled="!$hasProjectAccessFor([$pRole.ADMIN])"
                      @click="handleDialogOpen"
                    >
                      <v-icon>mdi-content-copy</v-icon>
                      {{ $vuetify.lang.t('$vuetify.common.copy') }}
                    </v-btn>
                  </template>

                  <v-card class="d-flex flex-column" style="height: 80vh;">
                    <div class="px-3 py-3 primary--text font-weight-bold">
                      {{ $vuetify.lang.t('$vuetify.add_maira.copy_script_tag') }}
                    </div>
                    <div class="px-3 py-3 text-subtitle-1">
                      {{ $vuetify.lang.t('$vuetify.add_maira.copy_description') }}
                    </div>

                    <v-card-text class="flex-grow-1 overflow-auto">
                      <v-container class="px-0 pb-0">
                        <div
                          v-html="scriptContent()"
                          id="scriptContent"
                          ref="scriptContent"
                          style="font-family: monospace; white-space: pre-wrap;"
                        ></div>
                      </v-container>
                    </v-card-text>

                    <v-card-actions class="d-flex justify-end">
                      <v-btn
                        text
                        color="error"
                        @click="cancelCopy"
                        class="mr-2"
                      >
                        {{ this.$vuetify.lang.t('$vuetify.common.cancel') }}
                      </v-btn>
                      <v-btn
                        color="primary"
                        @click="copyScript"
                      >
                        {{ this.$vuetify.lang.t('$vuetify.common.copy') }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </div>
            </div>

            <v-dialog v-model="credentialDialog" width="500" persistent>
              <v-card>
                <div class="px-3 py-3 primary--text font-weight-bold">
                  {{ $vuetify.lang.t("$vuetify.project_credentials.add_credential_label_txt_for_maira_script") }}
                </div>
                <v-divider></v-divider>
                
                <v-form ref="credentialForm">
                  <v-layout class="px-5 py-5">
                    <v-row>
                      <v-col cols="12" md="12">
                        <v-text-field
                          v-model="credentialName"
                          :label="$vuetify.lang.t('$vuetify.project_credentials.credentials_name_header_txt')"
                          :rules="[
                            v => !!v || $vuetify.lang.t('$vuetify.project_credentials.credentials_error_msg_txt'),
                          ]"
                          outlined
                          dense
                        ></v-text-field>

                        <v-select
                          :label="$vuetify.lang.t('$vuetify.project_credentials.key_type_selector_txt')"
                          :items="credentialTypeOptions"
                          v-model="credentialType"
                          outlined
                          dense
                          disabled
                        >
                          <template v-slot:append>
                            <v-tooltip top>
                              <template v-slot:activator="{ on }">
                                <v-avatar v-on="on">
                                  <v-icon>mdi-information</v-icon>
                                </v-avatar>
                              </template>
                              <span>
                                {{ $vuetify.lang.t("$vuetify.project_credentials.client_key_tooltip_txt") }}
                              </span>
                            </v-tooltip>
                            <v-icon>mdi-chevron-down</v-icon>
                          </template>
                        </v-select>

                        <v-combobox
                          v-model="whitelistedDomainShow"
                          :label="$vuetify.lang.t('$vuetify.project_credentials.whitelisted_domain_label_txt')"
                          :placeholder="$vuetify.lang.t('$vuetify.project_credentials.whitelisted_domain_placeholder_txt')"
                          multiple
                          small-chips
                          outlined
                          append-icon=""
                          deletable-chips
                          clearable
                          height="90"
                          @keydown.space.prevent
                          :rules="[validateWhitelistedDomain()]"
                        >
                        </v-combobox>

                        <v-alert
                          v-if="whitelistedDomainShow.length === 0"
                          outlined
                          dense
                          prominent
                          text
                          border="left"
                          type="warning"
                        >
                          <span
                            v-html="$vuetify.lang.t('$vuetify.project_credentials.whitelisted_domain_security_alert_txt')"
                          ></span>
                        </v-alert>
                      </v-col>
                    </v-row>
                  </v-layout>
                </v-form>
                
                <v-divider></v-divider>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    text
                    class="red--text font-weight-bold"
                    color="red"
                    @click="closeCredentialDialog"
                  >
                    {{ $vuetify.lang.t('$vuetify.common.cancel_btn') }}
                  </v-btn>
                  <v-btn
                    class="font-weight-bold"
                    :disabled="!credentialFormValid"
                    :color="credentialFormValid ? 'primary' : '#90A4AE'"
                    @click="saveCredentialClick"
                    :loading="credentialSaving"
                  >
                    {{ $vuetify.lang.t('$vuetify.common.save_btn') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- Download Credential Modal -->
            <v-dialog v-model="successDialog" persistent width="600" @keydown.esc="handleDownloadModalClose">
              <v-card>
                <v-card-title class="headline white--text text--primary mb-4">
                  {{ $vuetify.lang.t("$vuetify.project_credentials.credentials_label_txt") }}
                </v-card-title>
                <v-card-text>
                  <div class="mb-8">
                    <v-alert type="info" outlined>
                      {{ $vuetify.lang.t("$vuetify.project_credentials.credentials_download_json_msg_txt") }}
                    </v-alert>
                  </div>
                  <!-- key copy box -->
                  <v-text-field
                    v-model="client_key"
                    :label="$vuetify.lang.t('$vuetify.project_credentials.client_key_label_txt')"
                    outlined
                    readonly
                  >
                    <template v-slot:append>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" @click="copyClientKey()"
                            >mdi-content-copy</v-icon
                          >
                        </template>
                        {{
                          client_key_copy
                            ? $vuetify.lang.t("$vuetify.common.copied_msg_txt")
                            : $vuetify.lang.t("$vuetify.common.copy_msg_txt")
                        }}
                      </v-tooltip>
                    </template>
                  </v-text-field>
                  <v-btn color="primary" @click="downloadCredential()">
                    <v-icon>mdi-download</v-icon>
                    {{ $vuetify.lang.t("$vuetify.project_credentials.download_json_msg_txt") }}
                  </v-btn>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    text
                    color="primary"
                    @click="handleDownloadModalClose"
                  >
                    {{ $vuetify.lang.t('$vuetify.common.close_btn') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

          <v-container>
            <v-row class="pl-4" dense>
              <v-col cols="12" xs="12" md="6">
                <v-text-field
                  v-model="chatHeaderText"
                  :label="$vuetify.lang.t('$vuetify.add_maira.chat_header_text')"
                  dense
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12" xs="12" md="6" class="align-self-center">
                <v-radio-group
                  v-model="chatHeaderAlign"
                  :label="''"
                  row
                  class="align-self-center ml-2"
                >
                  <v-radio :label="$vuetify.lang.t('$vuetify.add_maira.left')" value="left"></v-radio>
                  <v-radio :label="$vuetify.lang.t('$vuetify.add_maira.center')" value="center"></v-radio>
                  <v-radio :label="$vuetify.lang.t('$vuetify.add_maira.right')" value="right"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
            <!-- <v-row class="pl-4">
              <v-col cols="12" xs="12" md="6" class="py-0">
                <div style="display: inline-block">
                  <v-switch
                    v-model="showChatIcon"
                    :label="$vuetify.lang.t('$vuetify.add_maira.show_chat_icon')"
                  ></v-switch>
                </div>
              </v-col>
              <v-col cols="12" xs="12" md="6" class="py-0">
                <div style="display: inline-block">
                  <v-switch
                    
                    :label="$vuetify.lang.t('$vuetify.add_maira.show_mini_icon')"
                  ></v-switch>
                </div>
              </v-col>
            </v-row> -->
            <v-row class="pl-4">
              <v-col cols="12" xs="12" md="6">
                <v-textarea
                  v-model="greetingText"
                  :label="$vuetify.lang.t('$vuetify.add_maira.greeting_text')"
                  dense
                  outlined
                  :textarea-auto-height="true"
                  rows="2"
                  max-height="200"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-container>
        </v-card>

        <v-card  v-if="$isSectionEnabled('design_maira','change_styling')" class="elevation-0">
          <div class="d-flex space-between align-center">
            <v-col cols="5">
              <v-card-title class="font-weight-bold text-subtitle-1 text-sm-h6">
                {{ $vuetify.lang.t('$vuetify.add_maira.change_styling') }}
              </v-card-title>
            </v-col>

            <v-col class="d-flex justify-end mr-4">
              <v-btn
                text
                class="mt-2 align-self-center"
                color="error"
                @click="resetAll"
              >
                {{ $vuetify.lang.t('$vuetify.add_maira.reset_all') }}
              </v-btn>
            </v-col>
          </div>

          <!-- <v-divider></v-divider> -->
          <v-row dense class="pl-4">
            <v-col cols="12">
              <v-subheader class="text-subtitle-1 font-weight-medium pl-0">{{ $vuetify.lang.t('$vuetify.add_maira.theme') }}</v-subheader>
              <div class="colorContainer">
                <div
                  v-for="theme in colors"
                  :key="theme"
                  cols="1"
                  class="colorBox"
                  :style="{ backgroundColor: theme }"
                  @click="setTheme(theme)"
                ></div>
                <v-col cols="9">
                  <v-text-field
                    class="colorInput"
                    v-model="selectedTheme"
                    :label="$vuetify.lang.t('$vuetify.add_maira.enter_code')"
                    :rules="[v => isValidColor(v) || $vuetify.lang.t('$vuetify.add_maira.invalid_code')]"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col class="d-flex">
                  <v-btn
                    text
                    class="mt-2"
                    color="error"
                    @click="selectedTheme=default_theme"
                  >
                    {{ $vuetify.lang.t('$vuetify.add_maira.reset') }}
                  </v-btn>
                </v-col>
              </div>
            </v-col>
          </v-row>

          <v-row dense class="pl-4">
            <v-col cols="12">
              <v-subheader class="text-subtitle-1 font-weight-medium pl-0">{{ $vuetify.lang.t('$vuetify.add_maira.chat_icon_color') }}</v-subheader>
              <div class="colorContainer">
                <div
                  v-for="color in colors"
                  :key="color"
                  cols="1"
                  class="colorBox"
                  :style="{ backgroundColor: color }"
                  @click="setChatIconColor(color)"
                ></div>
                <v-col cols="9">
                  <v-text-field
                    class="colorInput"
                    v-model="chatIconColor"
                    :label="$vuetify.lang.t('$vuetify.add_maira.enter_code')"
                    :rules="[v => isValidColor(v) || $vuetify.lang.t('$vuetify.add_maira.invalid_code')]"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col class="d-flex">
                  <v-btn
                    text
                    class="mt-2"
                    color="error"
                    @click="chatIconColor=default_theme"
                  >
                    {{ $vuetify.lang.t('$vuetify.add_maira.reset') }}
                  </v-btn>
                </v-col>
              </div>
            </v-col>
          </v-row>
          <v-row dense class="pl-4">
            <v-col cols="11">
              <v-expansion-panels>
                <v-expansion-panel
                  v-for="(properties, componentName) in filteredComponents"
                  :key="componentName"
                >
                  <v-expansion-panel-header>
                    <v-subheader class="text-subtitle-1 font-weight-medium pl-0">
                      {{ $vuetify.lang.t(`$vuetify.add_maira.component_names.${componentName.toLowerCase().replace(/ /g, '_')}`) }}
                    </v-subheader>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row dense class="pl-4" v-for="(value, property) in properties" :key="property">
                      <v-col cols="12">
                        <v-subheader class="text-subtitle-1">{{ getTranslatedPropertyName(property) }}</v-subheader>
                        <div class="colorContainer">
                          <div
                            v-for="color in colors"
                            :key="color"
                            class="colorBox"
                            :style="{ backgroundColor: color }"
                            @click="updateComponent(componentName, property, color)"
                          ></div>
                          <v-col cols="9">
                            <v-text-field
                              class="colorInput"
                              v-model="components[componentName][property]"
                              :label="$vuetify.lang.t('$vuetify.add_maira.enter_code')"
                              :rules="[v => isValidColor(v) || $vuetify.lang.t('$vuetify.add_maira.invalid_code')]"
                              @input="updateComponent(componentName, property, components[componentName][property])"
                              outlined
                            ></v-text-field>
                          </v-col>
                          <v-col class="d-flex">
                            <v-btn
                              text
                              class="mt-2"
                              color="error"
                              @click="updateComponent(componentName, property, '')"
                            >
                              {{ $vuetify.lang.t('$vuetify.add_maira.reset') }}
                            </v-btn>
                          </v-col>
                        </div>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </v-card>
      </v-container>
      </v-col>

      <!-- Replace the existing container with this -->
      <v-col v-if="$isSectionEnabled('design_maira','customize_widget') || $isSectionEnabled('design_maira','change_styling')"  cols="12" sm="12" md="6" class="preview-column">
        <div class="preview-content" style="position: relative; height: 100%;">
          <div class="chat-wrapper" :class="{'mobile-chat': isMobile}" :style="mainContainerStyle">
            <v-card elevation="0" class="mx-auto chat-card" :width="isMobile ? '100%' : '400'" height="600">
              <!-- Header -->
              <v-app-bar 
                flat 
                dense 
                :color="components['Message Header']['--message-header-background-color']"
                :style="messageHeaderStyle"
              >
                <div 
                  class="header-title-wrapper"
                  :style="{ textAlign: chatHeaderAlign, width: '100%' }"
                >
                  <v-app-bar-title 
                    class="text-subtitle-1 font-weight-medium"
                    :style="{ color: components['Message Header']['--message-header-text-color'] }"
                  >
                    {{ chatHeaderText }}
                  </v-app-bar-title>
                </div>
                <v-spacer></v-spacer>
                <v-btn 
                  icon 
                  small 
                  v-if="showMinimizeIcon"
                  :color="components['Message Header']['--message-header-minus-color']"
                >
                  <v-icon>mdi-minus</v-icon>
                </v-btn>
              </v-app-bar>

              <v-divider
                :style="{ backgroundColor: components['Chatlist Header']['--chatlist-header-divider-color'] }"
              ></v-divider>

              <!-- Chat Messages -->
              <v-card-text
                :backgroundColor="this.components['Message List']['--messagelist-background-color'] || 'white'"
                class="pa-3 fill-height chat-container"
                :style="messageListStyle"
              >
                <div>
                  <template v-for="(message, index) in messages">
                    <!-- Bot Messages (Incoming) -->
                    <div v-if="message.isBot" :key="index" class="d-flex mb-4">
                      <v-avatar 
                        size="40" 
                        class="mr-2 mt-1"
                        :style="{
                          border: `2px solid ${components['Avatar'] && components['Avatar']['--incoming-message-avatar-border-color'] || selectedTheme}`,
                          overflow: 'hidden'
                        }"
                      >
                      <img :src="avatar" alt="bot avatar" />
                      </v-avatar>
                      <v-card 
                        flat 
                        :color="components['Incoming Message']['--incoming-message-background-color'] || 'light-blue lighten-3'"
                        class="message-card pa-2 rounded-lg"
                        :style="incomingMessageStyle"
                      >
                        <v-card-text 
                          class="pa-1 body-2"
                          :style="{ color: components['Incoming Message']['--incoming-message-text-color'] }"
                        >
                          {{ message.text }}
                          <a 
                            v-if="message.link" 
                            :href="message.link"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="message-link"
                            :style="incomingLinkStyle"
                            @click="handleLinkClick($event, message)"
                          >
                            {{ message.linkText || 'Link' }}
                          </a>
                          <div class="d-flex justify-end">
                            <span
                              :color="components['Incoming Message']['--incoming-message-timestamp-color'] || 'grey darken-1'" 
                              class="caption smaller-timestamp"
                              :style="incomingMessageTimeStamp"
                            >
                              {{ message.time }}
                            </span>
                          </div>
                        </v-card-text>
                      </v-card>
                      <v-btn 
                        icon 
                        x-small 
                        class="ml-1 mt-1" 
                        @click="copyMessage(message.text, index)"
                      >
                        <v-icon 
                          size="16" 
                          :color="selectedTheme || 'grey darken-1'"
                        >
                          {{ copiedMessageIndex === index ? 'mdi-check' : 'mdi-content-copy' }}
                        </v-icon>
                      </v-btn>
                    </div>

                    <!-- User Messages (Outgoing) -->
                    <div v-else :key="index" class="d-flex justify-end mb-4">
                      <v-card 
                        flat 
                        :color="components['Outgoing Message']['--outgoing-message-background-color'] || 'blue darken-1'"
                        class="message-card pa-2 rounded-lg"
                        :style="outgoingMessageStyle"
                      >
                        <v-card-text 
                          class="pa-1 body-2"
                          :style="{ color: components['Outgoing Message']['--outgoing-message-text-color'] }"
                        >
                          {{ message.text }}
                          <a 
                            v-if="message.link" 
                            :href="message.link"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="message-link"
                            :style="outgoingLinkStyle"
                            @click="handleLinkClick($event, message)"
                          >
                            {{ message.linkText || 'Link' }}
                          </a>
                          <div class="d-flex justify-end align-center">
                            <span
                              class="caption smaller-timestamp"
                              :style="outgoingMessageTimestampColor"
                            >
                              {{ message.time }}
                            </span>
                            <v-icon 
                              size="12" 
                              class="ml-1"
                              :color="components['Outgoing Message']['--outgoing-message-checkmark-color'] || 'grey lighten-1'"
                            >
                              mdi-check
                            </v-icon>
                          </div>
                        </v-card-text>
                      </v-card>
                    </div>
                  </template>

                </div>

                <!-- Product Carousel (Outside Message Response) -->
                <!-- <div v-if="lastBotMessage && lastBotMessage.products && lastBotMessage.products.length > 0" class="product-carousel-container mt-4">
                  <v-sheet class="mx-auto" elevation="0" flat color="transparent">
                    <v-slide-group
                      show-arrows
                      :color="components['Carousel']['--product-title-button-color'] || selectedTheme"
                    >
                      <v-slide-item
                        v-for="(product, i) in lastBotMessage.products"
                        :key="i"
                        v-slot="{ active, toggle }"
                      >
                        <v-card class="ma-2 d-flex flex-column justify-space-between" width="180" height="250" outlined color="transparent">
                          <v-img :src="product.image" height="130" contain class="product-image"></v-img>

                          <v-card-title class="text-body-2 pa-2"
                            :style="{ color: components['Carousel']['--product-title-text-color'] || 'inherit' }"
                          >
                            {{ product.title }}<br>{{ product.subtitle }}
                          </v-card-title>

                          <v-card-text class="text-body-2 pa-2 font-weight-bold"
                            :style="{ color: components['Carousel']['--product-title-price-color'] || selectedTheme }"
                          >
                            ¥{{ formatNum(product.price) }}
                          </v-card-text>

                          <v-card-actions class="pt-0">
                            <v-btn
                              text
                              small
                              block
                              :color="components['Carousel']['--product-title-button-color'] || selectedTheme"
                              :style="{ border: `1px solid ${components['Carousel']['--product-title-button-color'] || selectedTheme}` }"
                            >
                              {{ product.buttonText || '商品ページ' }}
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-slide-item>
                    </v-slide-group>
                  </v-sheet>
                </div> -->
                <!-- Typing Indicator -->
                <div v-if="isTyping" class="d-flex mb-4">
                    <div 
                      class="typing-indicator-card"
                      :style="incomingMessageStyle"
                    >
                      <div class="typing-indicator">
                        <span :style="{ backgroundColor: loaderColor }"></span>
                        <span :style="{ backgroundColor: loaderColor }"></span>
                        <span :style="{ backgroundColor: loaderColor }"></span>
                      </div>
                    </div>
                  </div>
              </v-card-text>

              <v-divider :style="{ backgroundColor: components['Chatlist Header']['--chatlist-header-divider-color'] }"></v-divider>

              <!-- Input Area -->
              <div class="px-3 py-3 d-flex align-center chat-input" :style="inputFieldStyle">
                <v-text-field
                  v-model="newMessage"
                  placeholder="Type message here"
                  hide-details
                  outlined
                  dense
                  class="mr-2 invisible-border"
                  :style="{
                    ...inputElementStyle, 
                    borderRadius: '12px',
                  }"
                  @keyup.enter="sendMessage"
                />

                <v-btn icon @click="sendMessage">
                  <v-icon :color="components['Input Field']['--input-send-color'] || '#B0C4DE'">mdi-send</v-icon>
                </v-btn>
              </div>
            </v-card>
            <!-- Custom Chat Icon Button -->
            <div
              class="chat-icon-wrapper"
              @click="toggleChat"
              :class="{'mobile-icon': isMobile}"
              :style="{ backgroundColor: chatIconColor }"
            >
              <!-- Chat Icon (shown when chat is closed) -->
              <div v-if="!chatOpen" class="chat-svg-icon">
                <svg viewBox="0 0 24 24" width="30" height="30" xmlns="http://www.w3.org/2000/svg">
                  <g id="SVGRepo_iconCarrier">
                    <!-- Keep the speech bubble white -->
                    <path 
                      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 13.5997 2.37562 15.1116 3.04346 16.4525C3.22094 16.8088 3.28001 17.2161 3.17712 17.6006L2.58151 19.8267C2.32295 20.793 3.20701 21.677 4.17335 21.4185L6.39939 20.8229C6.78393 20.72 7.19121 20.7791 7.54753 20.9565C8.88837 21.6244 10.4003 22 12 22Z" 
                      fill="white">
                    </path>

                    <!-- Dots matching background color -->
                    <path 
                      d="M15 12C15 12.5523 15.4477 13 16 13C16.5523 13 17 12.5523 17 12C17 11.4477 16.5523 11 16 11C15.4477 11 15 11.4477 15 12Z" 
                      :fill="chatIconColor">
                    </path>
                    <path 
                      d="M11 12C11 12.5523 11.4477 13 12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12Z" 
                      :fill="chatIconColor">
                    </path>
                    <path 
                      d="M7 12C7 12.5523 7.44772 13 8 13C8.55228 13 9 12.5523 9 12C9 11.4477 8.55228 11 8 11C7.44772 11 7 11.4477 7 12Z" 
                      :fill="chatIconColor">
                    </path>
                  </g>
                </svg>
              </div>
              
              <!-- Close Icon (shown when chat is open) -->
              <div v-else class="close-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M6 6L18 18" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
      </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";

import store from "@/store";

import {
  REFRESHTOKEN,
  ADD_ALERT,
  GET_MAIRA_OPTIONS,
  UPDATE_MAIRA_OPTIONS,
  LOAD_ALL_CREDENTIAL,
  SAVE_CREDENTIAL
} from "@/store/_actiontypes";
import { formatNum, capitalizeFirstLetter, formatDateTime } from "@/helpers/helper";
import getEnv from "@/config/env";
import Avatar from "../../assets/images/maira-advisor-icon.png";

const DEFAULT_THEME = '#0693E3';

export default {
  props: {
    tabName: {
      type: String,
      required: false,
    },
    activateCopyScript: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  components: {
  },
  data: () => ({
    avatar: Avatar,
    chatOpen: true,
    iconHovered: false,
    isMobile: false,
    windowWidth: window.innerWidth,
    formatNum,
    capitalizeFirstLetter,
    formatDateTime,
    isLoading: false, 
    confirmDialog: false,
    showChatIcon: true,
    chatHeaderText: 'AI Advisor',
    greetingText: 'Hi, I am Maira. How can I help you today?',
    chatHeaderAlign: 'left',
    mairaBaseUrl: getEnv("VUE_APP_MAIRA_SRC"),
    apiInstance: null, // To store the widgetApi instance
    showMinimizeIcon: true,
    copyScriptTag: false,
    colors: ['#FF6900', '#FCB900', '#7BDCB5', '#00D084', '#8ED1FC', '#0693E3', '#ABB8C3', '#EB144C', '#F78DA7', '#9900EF'],
    selectedTheme: DEFAULT_THEME,
    customTheme: '',
    chatIconColor: DEFAULT_THEME,
    components: {
      'Message Header': {
        "--message-header-background-color": "",
        "--message-header-text-color": "",
        // "--message-header-last-active-color": "",
        // "--message-header-back-color": "",
        // "--message-header-minus-color": ""
      },
      'Message List': {
        "--messagelist-background-color": "",
        // "--no-message-text-color": "",
      },
      'Incoming Message': {
        "--incoming-message-text-color": "",
        // "--incoming-message-name-text-color": "",
        "--incoming-message-background-color": "",
        "--incoming-message-timestamp-color": "",
        "--incoming-message-link-color": "",
      },
      'Outgoing Message': {
        "--outgoing-message-text-color": "",
        "--outgoing-message-background-color": "",
        "--outgoing-message-timestamp-color": "",
        "--outgoing-message-checkmark-color": "",
        // "--outgoing-message-loader-color": "",
        "--outgoing-message-link-color": "",
      },
      'Carousel': {
        // "--product-title-text-color": "",
        // "--product-title-price-color": "",
        // "--product-title-button-color": "",
      },
      'Input Field': {
        "--input-background-color": "",
        "--input-text-color": "",
        "--input-element-color": "",
        // "--input-attach-color": "",
        "--input-send-color": "",
        "--input-placeholder-color": "",
      },
      'Chatlist Header': {
        // "--chatlist-header-background-color": "",
        // "--chatlist-header-text-color": "",
        // "--chatlist-header-divider-color": "",
      },
      'Chatlist': {
        // "--chatlist-background-color": "",
        // "--no-conversation-text-color": "",
      },
      'Chat Item': {
        // "--chatitem-background-color": "",
        // "--chatitem-selected-background-color": "",
        // "--chatitem-title-text-color": "",
        // "--chatitem-content-text-color": "",
        // "--chatitem-hover-color": "",
      },
      'Loader': {
        "--loader-color": "",
      },
      'Main Container': {
        "--container-background-color": "",
      },
    },
    default_theme: DEFAULT_THEME,
    messages: [
      { 
        text: 'How can I help you ?', 
        isBot: true, 
        time: 'just now',
      },
      { text: 'hi how are you doing and what is your name?', isBot: false, time: '2d' },
      { text: 'Hello! I am your AI Advisor. How can I assist you today?', isBot: true, time: '2d' },
      { 
        text: 'I need help with the integration guide', 
        isBot: false, 
        time: '1d',
        link: '#',
        linkText: 'Integration Guide',
        isPreview: true
      },
      { 
        text: 'You can find detailed tutorials here:', 
        isBot: true, 
        time: '1d',
        link: '#',
        linkText: 'Step-by-step Tutorials',
        isPreview: true
      }
      // { text: 'Do you have any sake recommendations?', isBot: false, time: '2d' },
      // { 
      //   text: 'Yes, I have some excellent sake recommendations for you!', 
      //   isBot: true, 
      //   time: '2d',
      //   // products: [
      //   //   {
      //   //     title: 'Cyclear Face Wash',
      //   //     subtitle: '120g',
      //   //     price: 980,
      //   //     image: require('@/assets/images/bottle1.jpg'),
      //   //     buttonText: '商品ページ'
      //   //   },
      //   //   {
      //   //     title: 'Salon Link Amino',
      //   //     subtitle: '1000ml',
      //   //     price: 3390,
      //   //     image: require('@/assets/images/bottle2.png'),
      //   //     buttonText: '商品ページ'
      //   //   },
      //   //   {
      //   //     title: 'Daiso Cotton Puff',
      //   //     subtitle: '130 Pcs',
      //   //     price: 380,
      //   //     image: require('@/assets/images/bottle3.png'),
      //   //     buttonText: '商品ページ'
      //   //   },
      //   //   {
      //   //     title: 'Kose Suncut R Tone Up',
      //   //     subtitle: '80g',
      //   //     price: 1280,
      //   //     image: require('@/assets/images/bottle4.jpg'),
      //   //     buttonText: '商品ページ'
      //   //   }
      //   // ]
      // }
    ],
    newMessage: '',
    isTyping: false,
    copiedMessageIndex: null,
    credentialDialog: false,
    valid: false,
    credentialName: '',
    credentialType: 'client',
    whitelistedDomainShow: [],
    whitelistedDomain: {},
    isValidWhitelistedDomain: true,
    credentialResponse: null,
    edit: false,
    toBeUpdatedItem: null,
    credentialSaving: false,
    successDialog: false,
    client_key: '',
    client_key_copy: false,
  }),
  created() {
  this.getAllCredentialFunction();
},
  computed: {
    ...mapState({
      selectedProject: (state) => state.project.selectedProject,
      user: (state) => state.account.user,
      allCredential: (state) => state.project.allCredential,
      activeClientKey() {
        let clientCredential = this.allCredential.find(
            credential => credential.key_type === 'client' && credential.status === 'active'
        );
        return clientCredential;
      },
    }),
    credentialTypeOptions() {
      return [
        {
          text: this.$vuetify.lang.t("$vuetify.project_credentials.credential_type.client_key_txt"),
          value: "client"
        }
      ];
    },
    
    // Form validation
    validateForm() {
      return this.credentialName && this.credentialName.trim() !== '';
    },
    credentialFormValid: {
      get() {
        return this.credentialName && 
              this.credentialName.trim() !== '' && 
              this.isValidWhitelistedDomain;
      },
      set(value) {}
    },
    filteredComponents() {
      const excludeComponents = ['Carousel', 'Chatlist Header', 'Chatlist', 'Chat Item'];
      return Object.fromEntries(
        Object.entries(this.components).filter(([key]) => !excludeComponents.includes(key))
      );
    },
    mainContainerStyle() {
      return {
        backgroundColor: this.components['Main Container']['--container-background-color'] || 'white',
      };
    },
    messageHeaderStyle() {
      return {
        backgroundColor: this.components['Message Header']['--message-header-background-color'],
      };
    },
    messageListStyle() {
      return {
        backgroundColor: this.components['Message List']['--messagelist-background-color'],
      };
    },
    incomingMessageStyle() {
      return {
        backgroundColor: this.components['Incoming Message']['--incoming-message-background-color'],
      };
    },
    incomingMessageTimeStamp() {
      return {
        color: this.components['Incoming Message']['--incoming-message-timestamp-color'],
      };
    },
    outgoingMessageStyle() {
      return {
        backgroundColor: this.components['Outgoing Message']['--outgoing-message-background-color'],
      };
    },
    inputFieldStyle() {
      return {
        backgroundColor: this.components['Input Field']['--input-background-color'],
      };
    },
    inputElementStyle() {
      return {
        backgroundColor: this.components['Input Field']['--input-element-color'],
        color: this.components['Input Field']['--input-text-color'],
      };
    },
    lastBotMessage() {
      return this.messages.filter(msg => msg.isBot).pop() || null;
    },
    loaderColor() {
      return this.components['Loader']['--loader-color'] || 
            this.selectedTheme || 
            '#3232c9';
    },
    outgoingMessageTimestampColor() {
      return {
        color: this.components['Outgoing Message']['--outgoing-message-timestamp-color'],
        borderColor: this.components['Outgoing Message']['--outgoing-message-loader-color'] || 'grey darken-1'
      };
    },
    incomingLinkStyle() {
      return {
        color: this.components['Incoming Message']['--incoming-message-link-color'] || '#1976d2',
        textDecoration: 'underline',
        fontWeight: '500'
      };
    },
    
    outgoingLinkStyle() {
      return {
        color: this.components['Outgoing Message']['--outgoing-message-link-color'] || '#bbdefb',
        textDecoration: 'underline',
        fontWeight: '500'
      };
    }
  },
  watch: {
    whitelistedDomainShow: {
      handler: function () {
        if (this.whitelistedDomainShow != null) {
          this.validateWhitelistedDomain();
        } else {
          console.warn("whitelistedDomainShow is null or undefined");
        }
      },
      deep: true,
    },
    // showChatIcon(newVal) {
    //   this.sendOptions();
    // },
    chatHeaderText(newVal) {
      this.sendOptions();
    },
    chatHeaderAlign(newVal) {
      this.sendOptions();
    },
    // showMinimizeIcon(newVal) {
    //   this.sendOptions();
    // },
    components: {
      handler(newVal, oldVal) {
        this.sendConfig();
      },
      deep: true
    },
    selectedTheme(newVal) {
      this.sendConfig();
    },
    chatIconColor(newVal) {
      this.sendOptions();
    },
    greetingText(newVal) {
      this.sendOptions();
      if (this.messages.length > 0 && this.messages[0].isBot) {
        this.$set(this.messages, 0, {
          ...this.messages[0],
          text: newVal.trim() === '' ? 'How can I assist you today?' : newVal
        });
      }
    },
    tabName(newVal) {
      this.handleTabChange(newVal);
    },
    activateCopyScript(newVal) {
      if(newVal){
        this.copyScriptTag = true;
      }
      else {
        this.copyScriptTag = false;
      }
    },
  },
  mounted() {
    this.getAllCredentialFunction();
    this.getMairaOptions();
    this.$root.$on('tab-change', this.handleTabChange);
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize);
  },

  methods: {
    ...mapActions("alert", [ADD_ALERT]),
    ...mapActions("account", [REFRESHTOKEN]),
    ...mapActions("project", [
      LOAD_ALL_CREDENTIAL,
      GET_MAIRA_OPTIONS,
      UPDATE_MAIRA_OPTIONS,
      SAVE_CREDENTIAL
    ]),
    checkScreenSize() {
      this.windowWidth = window.innerWidth;
      this.isMobile = window.innerWidth < 960;
    },
    resetAll() {
      this.selectedTheme = DEFAULT_THEME;
      this.chatIconColor = DEFAULT_THEME;
      for (const componentName in this.components) {
        if (Object.prototype.hasOwnProperty.call(this.components, componentName)) {
          const component = this.components[componentName];
          for (const property in component) {
            if (Object.prototype.hasOwnProperty.call(component, property)) {
              this.updateComponent(componentName, property, '')
            }
          }
        }
      }
    },
    switchToSettingsTab() {
      if(!this.activeClientKey) {
        this.$emit('activate-settings-tab');
      }
    },
    async getAllCredentialFunction() {
    try {
      await this.LOAD_ALL_CREDENTIAL({
        project_id: this.selectedProject.id,
        key_type: 'client'
      });
      this.isCredentialsLoaded = true;
    } catch (error) {
      this.isCredentialsLoaded = false;
    }
  },
    handleTabChange(newTab) {
      this.copyScriptTag =  false;
      if (newTab === 'designMaira') {
        this.getAllCredentialFunction();
      }
    },
    getMairaOptions(){
      this.GET_MAIRA_OPTIONS({
        project_id: this.$route.query.pid,
      }).then(
          (response) => {
            if (response.data.maira_options &&
                typeof response.data.maira_options === 'object') {
              // if ('showMinimizeIcon' in response.data.maira_options) {
              //   this.showMinimizeIcon = response.data.maira_options.showMinimizeIcon;
              // }
              // if ('showChatIcon' in response.data.maira_options) {
              //   this.showChatIcon = response.data.maira_options.showChatIcon;
              // }
              if ('chatHeaderText' in response.data.maira_options) {
                this.chatHeaderText = response.data.maira_options.chatHeaderText;
              }
              if ('greetingText' in response.data.maira_options) {
                this.greetingText = response.data.maira_options.greetingText;
              }
              if ('chatHeaderAlign' in response.data.maira_options) {
                this.chatHeaderAlign = response.data.maira_options.chatHeaderAlign;
              }
              if ('chatIconColor' in response.data.maira_options) {
                this.chatIconColor = response.data.maira_options.chatIconColor;
              }
              if ('selectedTheme' in response.data.maira_options) {
                this.selectedTheme = response.data.maira_options.selectedTheme;
              }
              // Iterate through the components and update the inner attributes if present
              for (let component in this.components) {
                if (response.data.maira_options.components && response.data.maira_options.components[component]) {
                  for (let property in this.components[component]) {
                    if (property in response.data.maira_options.components[component]) {
                      this.$set(this.components[component], property, response.data.maira_options.components[component][property]);
                    }
                  }
                }
              }

              this.sendOptions();
              this.sendConfig();
            }
          },
          (error) => error
      );
    },
    updateMairaOptions(){
      this.UPDATE_MAIRA_OPTIONS({
        project_id: this.$route.query.pid,
        mairaOptions: {
          // showMinimizeIcon: this.showMinimizeIcon,
          // showChatIcon: this.showChatIcon,
          chatHeaderText: this.chatHeaderText,
          chatHeaderAlign: this.chatHeaderAlign,
          greetingText: this.greetingText,
          chatIconColor: this.chatIconColor,
          components: this.components,
          selectedTheme: this.selectedTheme,
        },
      }).then(
          (response) => {
            store.dispatch(
                `alert/${ADD_ALERT}`,
                {
                  message: this.$vuetify.lang.t('$vuetify.add_maira.options_added_successfully'),
                  color: "success",
                },
                { root: true }
            );
          },
          (error) => {
            store.dispatch(
                `alert/${ADD_ALERT}`,
                { message: "Update failed!", color: "error" },
                { root: true }
            );
          }
      );
    },
    sendConfig() {
      const customThemeSet = {};

      // Iterate through Components object
      for (const componentKey in this.components) {
        if (Object.prototype.hasOwnProperty.call(this.components, componentKey)) {
          const component = this.components[componentKey];
          // Iterate through properties of each component
          for (const propertyKey in component) {
            if (Object.prototype.hasOwnProperty.call(component, propertyKey)) {
              const value = component[propertyKey];
              // Set CSS variable
              if (value) {
                document.documentElement.style.setProperty(propertyKey, value);
                customThemeSet[propertyKey] = value;
              } else {
                document.documentElement.style.removeProperty(propertyKey);
              }
            }
          }
        }
      }

      const customTheme = this.selectedTheme;

      const config = {
        colorSet: customThemeSet,
      };

      if (customTheme) {
        config.theme = customTheme;
      }

      this.applyConfig(config);
    },
    sendOptions() {
      const options = {
        // showChatIcon: this.showChatIcon,
        chatHeaderText: this.chatHeaderText,
        // showMinimizeIcon: this.showMinimizeIcon,
        chatHeaderAlign: this.chatHeaderAlign,
        greetingText: this.greetingText,
      };

      // Only add chatIconColor if it's present
      if (this.chatIconColor) {
        options.chatIconColor = this.chatIconColor;
      }

      this.applyOptions(options);
    },
    cancelCopy() {
      this.copyScriptTag = false;
    },
    setChatIconColor(color){
      this.chatIconColor = color;
    },
    updateComponent(componentKey, property, value) {
      if (Object.prototype.hasOwnProperty.call(this.components, componentKey) && Object.prototype.hasOwnProperty.call(this.components[componentKey], property)) {
        this.components[componentKey][property] = value;
      }
    },
    scriptContent() {
      const scriptString = `
        <script type="text/javascript">
          (function() {
            const clientKey = 'CLIENT_KEY';
            var s1 = document.createElement("script"),
                s0 = document.getElementsByTagName("script")[0];

            s1.async = true;
            s1.src = '${this.mairaBaseUrl}';
            s1.setAttribute('data-greeting', '${this.greetingText.trim() === "" ? "How can I help you?" : this.greetingText}');
            s1.setAttribute('data-client-key', clientKey);
            s1.setAttribute('data-icon-color', '${this.chatIconColor}');
            s1.setAttribute('data-chat-header','${this.chatHeaderText}');
            s1.setAttribute('data-chat-header-align','${this.chatHeaderAlign}');
            s1.setAttribute('data-prefix', '${window.location.hostname}');

            s0.parentNode.insertBefore(s1, s0);
          })();
        <\/script>
        <script type="text/javascript">
          (function () {
            function widgetApi() {
              return new Promise((resolve) => {
                let timeoutId;
                const getApi = () => {
                  const event = new Event('getWidgetApi');
                  timeoutId = window.setTimeout(getApi, 1000);
                  window.dispatchEvent(event);
                };
                const onWidgetApi = (e) => {
                  const api = e.detail;
                  window.clearTimeout(timeoutId);
                  resolve(api);
                };
                window.addEventListener('widgetApi', onWidgetApi, { once: true });
                getApi();
              });
            }
            ${this.generateCustomColorSetScript()}
          })();
        <\/script>`;
      // Convert special characters to HTML entities for proper rendering in HTML
      const formattedScript = 
        `<pre style="white-space: pre-wrap; word-wrap: break-word;">
            ${scriptString
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')}
        </pre>`;

      return formattedScript;
    },
    generateCustomColorSetScript() {
      const customThemeSet = {};
      for (const componentKey in this.components) {
        if (Object.prototype.hasOwnProperty.call(this.components, componentKey)) {
          const component = this.components[componentKey];
          for (const propertyKey in component) {
            if (Object.prototype.hasOwnProperty.call(component, propertyKey)) {
              const value = component[propertyKey];
              if (value) {
                customThemeSet[propertyKey] = value;
              }
            }
          }
        }
      }
      const customTheme = this.selectedTheme;

      let colorSetScript = '';
      if (Object.keys(customThemeSet).length) {
        colorSetScript = `const customColorSet = ${JSON.stringify(customThemeSet)};`;
      }

      let themeScript = '';
      if (customTheme) {
        themeScript = `const theme = '${customTheme}';`;
      }

      return `${colorSetScript}
      ${themeScript}
      widgetApi().then(api=>{
        ${themeScript ? 'api.sendConfig({' +
          `theme: '${customTheme}',` : 'api.sendConfig({'}
          colorSet: customColorSet
        });
      });`;
    },
    copyScript() {
      // Select the script content
      const scriptContent = this.$refs.scriptContent;

      // Check if scriptContent is valid
      if (!scriptContent) {
        console.error('Script content element not found.');
        return;
      }

      const scriptText = scriptContent.innerText.trim();
      const realScriptText = scriptText.replace(this.clientKeyStars, this.activeClientKey);


      navigator.clipboard.writeText(realScriptText);
      this.cancelCopy();
      store.dispatch(
          `alert/${ADD_ALERT}`,
          {
            message: this.$vuetify.lang.t('$vuetify.add_maira.script_coppied'),
            color: "success",
          },
          { root: true }
      );
    },
    setTheme(theme) {
      this.selectedTheme = theme;
    },
    getLastTwoWords(str) {
      let words = str.replace(/^-+/, '').split(/-|_/);

      const commonPrefixes = ['incoming', 'outgoing', 'message', 'messagelist', 'no', 'header', 'chat', 'chatlist', 'chatitem', 'input', 'product', 'title'];
      words = words.filter(word => !commonPrefixes.includes(word.toLowerCase()));

      let result;
      if (words.length >= 3) {
        result = words.slice(-3);
      } else if (words.length >= 2) {
        result = words.slice(-2);
      } else {
        result = words;
      }

      result = result.map(word => word.charAt(0).toUpperCase() + word.slice(1));

      return result.join(' ');
    },

    resetActivateCopyScript(){
      this.$emit('reset-activate-copy-script-prop');
    },
    applyConfig(config) {
      const root = document.documentElement;

      for (const componentKey in this.components) {
        if (Object.prototype.hasOwnProperty.call(this.components, componentKey)) {
          const component = this.components[componentKey];
          for (const propertyKey in component) {
            if (Object.prototype.hasOwnProperty.call(component, propertyKey)) {
              if (!component[propertyKey]) {
                root.style.removeProperty(propertyKey);
              }
            }
          }
        }
      }

      for (const [key, value] of Object.entries(config.colorSet)) {
        root.style.setProperty(key, value);
      }
      
      if (config.theme) {
        root.style.setProperty('--theme', config.theme);
      }
    },
    applyOptions(options) {
      this.chatHeaderText = options.chatHeaderText;
      this.chatHeaderAlign = options.chatHeaderAlign;
      // this.showMinimizeIcon = options.showMinimizeIcon;
      // this.showChatIcon = options.showChatIcon;
      this.greetingText = options.greetingText;
      if (options.chatIconColor) {
        this.chatIconColor = options.chatIconColor;
      }
    },
    sendMessage() {
      const messageText = this.newMessage;
      
      if (!messageText || messageText.trim() === '') return;
      
      this.messages.push({
        text: messageText,
        isBot: false,
        time: this.getCurrentTime()
      });
      
      this.newMessage = '';

      this.$nextTick(() => {
        this.scrollToBottom();
      });
      
      this.simulateBotResponse(messageText);
    },
    simulateBotResponse(userMessage) {
      this.isTyping = true;

      this.$nextTick(() => {
        this.scrollToBottom();
      });

      const responseTime = Math.floor(Math.random() * 2000) + 1000;
      
      setTimeout(() => {
        this.isTyping = false;
        
        // Generate a response based on user message
        const response = this.generateBotResponse(userMessage);

        if (response !== null) {
          this.messages.push({
            text: response.text || response,
            isBot: true,
            time: this.getCurrentTime(),
            link: response.link,
            linkText: response.linkText
          });
        }

        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }, responseTime);
    },
    generateBotResponse(userMessage) {
      const lowercaseMessage = userMessage.toLowerCase();
      
      // if (lowercaseMessage.includes('product') || 
      //     lowercaseMessage.includes('recommend') || 
      //     lowercaseMessage.includes('sake') ||
      //     lowercaseMessage.includes('alcohol') ||
      //     lowercaseMessage.includes('shop')) {
        
      //   const responseWithProducts = {
      //     text: "Here are some products I'd recommend:",
      //     isBot: true,
      //     time: this.getCurrentTime(),
      //     products: [
      //       {
      //         title: 'Daiso Cotton Puff',
      //         subtitle: '130 Pcs',
      //         price: 380,
      //         image: require('@/assets/images/bottle3.png'),
      //         buttonText: '商品ページ'
      //       },
      //       {
      //         title: 'Cyclear Face Wash',
      //         subtitle: '120g',
      //         price: 980,
      //         image: require('@/assets/images/bottle1.jpg'),
      //         buttonText: '商品ページ'
      //       },
      //       {
      //         title: 'Salon Link Amino',
      //         subtitle: '1000ml',
      //         price: 3390,
      //         image: require('@/assets/images/bottle2.png'),
      //         buttonText: '商品ページ'
      //       },
      //       {
      //         title: 'Kose Suncut R Tone Up',
      //         subtitle: '80g',
      //         price: 1280,
      //         image: require('@/assets/images/bottle4.jpg'),
      //         buttonText: '商品ページ'
      //       }
      //     ]
      //   };
        
      //   this.messages.push(responseWithProducts);
      //   return null;
      // }
      
      if (lowercaseMessage.includes('hello') || lowercaseMessage.includes('hi')) {
        return { text: "Hello! How can I help you today?" };
      } else if (lowercaseMessage.includes('bye') || lowercaseMessage.includes('goodbye')) {
        return { text: "Goodbye! Have a great day!" };
      } else if (lowercaseMessage.includes('thank')) {
        return { text: "You're welcome! Is there anything else I can help with?" };
      } else if (lowercaseMessage.includes('help')) {
        return { text: "I'm here to help! Please let me know what you need assistance with." };
      } else if (lowercaseMessage.includes('name')) {
        return { text: `My name is ${this.chatHeaderText}. How can I assist you?` };
      } else if (lowercaseMessage.includes('time')) {
        return { text: `The current time is ${new Date().toLocaleTimeString()}.` };
      } else if (userMessage.endsWith('?')) {
        return { text: "That's a good question. Let me get back to you on that shortly." };
      } else if (lowercaseMessage.includes('link') || lowercaseMessage.includes('website')) {
        return {
          text: "Here is a helpful link for you:",
          link: "https://gigalogy.com/maira-use-case/",
          linkText: "Visit our use case page"
        };
      } else if (lowercaseMessage.includes('documentation') || lowercaseMessage.includes('docs')) {
        return {
          text: "You can find our documentation here:",
          link: "https://docs.gigalogy.com",
          linkText: "View Documentation"
        };
      } else {
        // Default responses (randomly selected)
        const defaultResponses = [
          { text: "I understand. Let me know if you have any questions." },
          { text: "That's interesting! Tell me more." },
          { text: "I see. Is there anything else you'd like to discuss?" },
          { text: "Thanks for sharing that information." },
          { text: "I'm here to help if you need anything specific." }
        ];
        return defaultResponses[Math.floor(Math.random() * defaultResponses.length)];
      }
    },
    getCurrentTime() {
      const now = new Date();
      return now.getHours() + ":" + String(now.getMinutes()).padStart(2, '0');
    },
    
    scrollToBottom() {
      const chatContainer = this.$el.querySelector('.chat-container');
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    },
    
    copyMessage(text, index) {
      // Copy the text to clipboard
      navigator.clipboard.writeText(text)
        .then(() => {
          this.copiedMessageIndex = index;
          
          setTimeout(() => {
            this.copiedMessageIndex = null;
          }, 2000);
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
        });
    },
    toggleChat() {
      this.chatOpen = !this.chatOpen;
    },
    isValidColor(str) {
      if (!str || str.trim() === '') {
        return true;
      }
      const s = new Option().style;
      s.color = str;
      return s.color !== '';
    },
    handleLinkClick(event, message) {
      if (message.isPreview) {
        event.preventDefault();
        return false;
      }
    },
    getTranslatedPropertyName(property) {
      const key = property.replace(/^--/, '').replace(/-/g, '_').toLowerCase();
      return this.$vuetify.lang.t(`$vuetify.add_maira.${key}`);
    },
    openCredentialDialog() {
      if (this.selectedProject.status === "running") {
        this.resetForm();
        this.edit = false;
        this.credentialName = "";
        this.credentialType = "client";
        this.whitelistedDomain = {};
        this.whitelistedDomainShow = [];
        this.credentialResponse = null;
        this.credentialDialog = true;
    
      // Reset form validation in next tick
      this.$nextTick(() => {
        if (this.$refs.credentialForm) {
          this.$refs.credentialForm.resetValidation();
        }
      });

      } else if (this.selectedProject.status === "locked") {
        this.ADD_ALERT({
          message: "Project is locked. Cannot create credentials.",
          color: "error"
        });
      } else {
        this.ADD_ALERT({
          message: "Project status does not allow credential creation.",
          color: "error"
        });
      }
    },
    
    closeCredentialDialog() {
      this.credentialDialog = false;
      this.resetForm();
    },
    
    resetForm() {
      this.credentialName = '';
      this.credentialType = 'client';
      this.whitelistedDomainShow = [];
      this.whitelistedDomain = {};
      this.isValidWhitelistedDomain = true;
      this.valid = false;
      this.edit = false;
      this.toBeUpdatedItem = null;
      
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    
    validateWhitelistedDomain() {
      return v => {
        let domains = Array.isArray(this.whitelistedDomainShow)
          ? this.whitelistedDomainShow
          : [];

        if (
          ("project" === this.credentialType ||
            "client" === this.credentialType) &&
          0 === domains.length
        ) {
          return true;
        }

        const domainPattern =
          /^(?!:\/\/)(?=.{1,255}$)(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}|localhost|\b((25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\b)$/;
        const isValid = domains.every(domain => {
          return domainPattern.test(domain);
        });

        this.isValidWhitelistedDomain = isValid;
        if (!isValid) {
          this.valid = false;
        }

        return (
          isValid ||
          this.$vuetify.lang.t("$vuetify.project_credentials.whitelisted_domain_validation_txt")
        );
      };
    },
    
    saveCredentialClick() {
      if (this.credentialFormValid && this.isValidWhitelistedDomain) { 
        let domains = Array.isArray(this.whitelistedDomainShow)
          ? this.whitelistedDomainShow
          : [];
        this.whitelistedDomain = {
          whitelists: domains,
        };
        
        if (this.selectedProject.id) {
          this.saveCredential({
            project_id: this.selectedProject.id,
            name: this.credentialName,
            type: this.credentialType,
            whitelisted: this.whitelistedDomain,
          });
        }
      }
    },
    
    async saveCredential(payload) {
      this.isLoading = true;
      this.credentialSaving = true;
      
      try {
        const response = await this.SAVE_CREDENTIAL(payload);
        this.credentialResponse = response.data.details;
        
        this.client_key = response.data.details.client_key;
        
        // Refresh credentials list
        await this.getAllCredentialFunction();
        
        this.closeCredentialDialog();
        
        this.showDownloadModal(response.data);
        
      } catch (error) {
        console.error('Error creating credential:', error);
      } finally {
        this.credentialSaving = false;
        this.isLoading = false;
      }
    },
    
    downloadCredential() {
      let json_object = { client_key: this.client_key };
      
      const credentialName = this.activeClientKey?.name || this.credentialName || 'client-key';
      
      let file_name = `${
        credentialName.indexOf(" ") >= 0
          ? credentialName.trim().replace(/\s/g, "-")
          : credentialName
      }-${this.$route.query.pid}.json`;
      
      if (!window.navigator.msSaveOrOpenBlob) {
        // BLOB NAVIGATOR
        const url = window.URL.createObjectURL(
          new Blob([JSON.stringify(json_object)]),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file_name);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // BLOB FOR EXPLORER 11
        window.navigator.msSaveOrOpenBlob(
          new Blob([JSON.stringify(json_object)]),
          file_name,
        );
      }
    },
    
    handleDialogOpen(event) {
      if (!this.activeClientKey) {
        this.copyScriptTag = false;
        this.openCredentialDialog();
      } else {
        this.copyScriptTag = true;
      }
    },
    copyClientKey() {
      navigator.clipboard.writeText(this.client_key);
      this.client_key_copy = true;
      setTimeout(() => {
        this.client_key_copy = false;
      }, 2000);
    },
    
    
    showDownloadModal(credentialResponse) {
      if (credentialResponse.client_key) {
        this.client_key = credentialResponse.client_key;
        this.credentialType = 'client';
      }
      this.credentialResponse = credentialResponse;
      this.successDialog = true;
    },
    handleDownloadModalClose() {
      this.successDialog = false;
      if (this.activeClientKey) {
        this.copyScriptTag = true;
      }
    },
  },
};
</script>

<style>
.app-container {
  display: flex;
  min-height: 100vh;
}

.scrollable-controls {
  height: 100vh;
  overflow-y: auto;
  padding: 20px;
}

.preview-column {
  background-color: #F5F5F5;
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Prevents content from overflowing */
}

/* Chat widget positioning */
.chat-wrapper {
  position: relative;
  margin: 5px auto;
  padding: 10px;
  border-radius: 15px;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);
  max-width: 400px;
}

.colorContainer {
  width: 100%;
  max-width: 600px;
  padding: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.colorBox {
  width: 40px;
  height: 40px;
  cursor: pointer;
  border-radius: 50% !important;
  margin: 2px;
}
.colorInput {
  margin-top: 10px;
  padding: 4px;
  width: calc(100% - 12px);
}

.v-input--selection-controls {
  margin-top: 0px !important;
}

.typing-indicator-card {
  min-width: 70px;
  background: none !important; /* Removes background */
  padding: 0 !important; /* Removes padding from card */
  display: flex; /* Ensure container stays stable */
  align-items: center;
}

.typing-indicator {
  display: inline-flex;
  align-items: center;
  padding: 6px 8px;
  height: 20px;
}

.typing-indicator span {
  height: 5px;
  width: 5px;
  float: left;
  margin: 0 2px;
  display: block;
  border-radius: 50%;
  opacity: 0.4;
  position: relative;
}

.typing-indicator span:nth-of-type(1) {
  animation: 0.6s bounce infinite 0.1s ease-in-out;
}

.typing-indicator span:nth-of-type(2) {
  animation: 0.6s bounce infinite 0.2s ease-in-out;
}

.typing-indicator span:nth-of-type(3) {
  animation: 0.6s bounce infinite 0.3s ease-in-out;
}

@keyframes bounce {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px); /* Slightly more travel for a wave-like feel */
  }
  100% {
    transform: translateY(0);
  }
}

.message-card {
  max-width: 70%;
  word-break: break-word;
}

.product-carousel-container {
  width: 100%;
  padding: 0;  /* Remove horizontal padding */
  margin-left: 0 !important; /* Force left margin to be 0 */
  /* text-align: left;
  display: flex;
  justify-content: flex-start; */
}

.product-image {
  background-color: white !important;
  border-radius: 5px !important;
}

.v-slide-group__wrapper {
  overflow-x: auto !important;
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

.v-slide-group__wrapper::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.v-card-actions {
  display: flex !important;
  justify-content: center !important;
}

.input-wrapper {
  background-color: #D6E5F3 !important;
  padding: 0 8px;
}

.chat-input >>> .v-input__slot {
  box-shadow: none !important;
  min-height: 36px !important;
}

.chat-input >>> input::placeholder {
  color: #718096;
  font-size: 14px;
}

/* Hide scrollbar while maintaining functionality */
.chat-container {
  overflow-y: auto;
  max-height: 488px;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.chat-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.smaller-timestamp {
  font-size: 0.65rem !important; /* Smaller than regular caption (0.75rem) */
  line-height: 1; /* Adjust line height for better spacing */
}

.v-col {
  position: relative;
}

.sticky-chat {
  margin-top: 5px;
  padding: 10px; /* Space for the border */
  border-radius: 15px; /* Soft rounded edges */
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);
  max-width: calc(500px - 80px);
  position: relative;
  left: 200px;
}

.chat-card {
  background: #f3f4f6 !important; 
  border-radius: 20px;
  box-shadow: none !important;
  filter: none !important;
}

.chat-icon-wrapper {
  position: absolute;
  bottom: -65px;
  right: 0;
  z-index: 9999;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  /* background-color: var(--chat-icon-color, #0052CC); */
  transition: transform 0.3s ease;
}

.chat-icon-wrapper:hover {
  transform: scale(1.05);
}

.chat-svg-icon, .close-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

@media (max-width: 959px) {
  /* For the controls column */
  .scrollable-controls {
    height: auto;
    min-height: 100vh;
  }
  
  /* For the chat widget column */
  .mobile-chat {
    position: absolute;
    bottom: 80px;
    left: 10px;
    right: 10px;
    max-width: none;
    width: calc(100% - 20px);
    margin: 0;
  }
  
  /* Keep the chat icon within the preview column */
  .mobile-icon {
    position: absolute !important;
    bottom: px !important;
    right: px !important;
  }
  
  /* Ensure the preview column has enough height on mobile */
  .preview-column {
    min-height: 80vh;
    position: relative;
  }
  
  .colorContainer {
    width: 100%;
    max-width: 600px;
  }
}

.chat-input >>> .v-text-field__slot input::placeholder {
  color: var(--input-placeholder-color) !important;
}

/* Add these to your <style> section */
.chat-input .v-text-field.v-text-field--solo .v-input__control .v-input__slot {
  background-color: var(--input-element-color) !important;
}

.chat-input .v-text-field.v-text-field--outlined .v-input__control .v-input__slot input {
  color: var(--input-text-color) !important;
}

.chat-input .v-text-field.v-text-field--outlined .v-input__control .v-input__slot input::placeholder {
  color: var(--input-placeholder-color) !important;
}

.invisible-border.v-text-field.v-text-field--outlined .v-input__control .v-input__slot fieldset {
  border-color: #96ceeb !important;
}

.header-title-wrapper {
  display: block; /* Ensure it’s not flex, so text-align works */
  flex-grow: 1; /* Allow it to take available space */
}
.v-app-bar-title {
  display: inline-block; /* Make the title respect text-align */
}
.message-link {
  word-break: break-word;
  text-decoration: underline;
  font-weight: 500;
  transition: opacity 0.2s ease;
}
.message-link:hover {
  opacity: 0.8;
}
.message-card a {
  display: inline-block;
  max-width: 100%;
}
.message-content {
  line-height: 1.4;
}
</style>